import type { JSX, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionListButton,
  ActionListLink,
  Banner,
  Card,
  typography,
} from "@dsaui/components";
import { ExternalLink } from "@dsaui/icons";
import { cn } from "@dsaui/utils";
import { PageShell } from "components/PageShell";
import { PageTitle } from "components/PageTitle";
import type { DashboardViewModel } from "pages/dashboard/utils";
import * as API from "lib/api/types";
import routes from "routes";

const externalLinkProps = {
  rel: "noopener noreferrer",
  target: "_blank",
};
const detailValueClassName = "text-body-sm m-0 wrap-anywhere text-gray-900";
const sectionHeadingClassName = cn("m-0", typography.formSectionHeading.className);
const sectionClassName = "flex flex-col gap-4 border-t border-gray-200 pt-7";

export interface MembershipPageLayoutProps {
  onCancelDues?: () => void;
  paymentHistory: API.PaymentHistoryItem[];
  viewModel: DashboardViewModel;
}

type MembershipActionKey =
  | "updateDues"
  | "requestWaiver"
  | "cancelDues"
  | "requestRefund"
  | "requestMembershipCard";

type MembershipAction = {
  href: string;
  label: string;
};

const defaultActionKeys: MembershipActionKey[] = [
  "updateDues",
  "requestWaiver",
  "cancelDues",
  "requestRefund",
  "requestMembershipCard",
];

const actionKeysByStatus: Partial<Record<API.MemberStatus, MembershipActionKey[]>> = {
  [API.MemberStatus.MEMBER]: defaultActionKeys,
  [API.MemberStatus.LAPSED_MEMBER]: defaultActionKeys,
  [API.MemberStatus.FORMER_MEMBER]: defaultActionKeys,
  [API.MemberStatus.DECEASED]: ["cancelDues", "requestRefund"],
  [API.MemberStatus.MERGED]: [],
};

export const MembershipPageLayout = ({
  onCancelDues,
  paymentHistory,
  viewModel,
}: MembershipPageLayoutProps): JSX.Element => {
  const { t } = useTranslation();
  const actions: Record<MembershipActionKey, MembershipAction> = {
    updateDues: {
      href: routes.external.renew,
      label: t("membershipPage.actions.updateDues"),
    },
    requestWaiver: {
      href: routes.external.requestDuesWaiver,
      label: t("membershipPage.actions.requestWaiver"),
    },
    cancelDues: {
      href: routes.external.cancelDues,
      label: t("membershipPage.actions.cancelDues"),
    },
    requestRefund: {
      href: routes.external.refundPayment,
      label: t("membershipPage.actions.requestRefund"),
    },
    requestMembershipCard: {
      href: routes.external.membershipCard,
      label: t("membershipPage.actions.requestMembershipCard"),
    },
  };
  const membershipActionKeys = actionKeysByStatus[viewModel.memberStatus] ?? [];

  return (
    <PageShell className="max-w-3xl">
      <PageTitle>{t("pages.membership")}</PageTitle>
      {viewModel.membershipIsMerged && (
        <Banner variant="warning">
          <p className="m-0">{viewModel.mergedAccountMessage}</p>
        </Banner>
      )}

      <Card className="flex flex-col gap-8">
        <Banner>
          <p className="m-0">{t("membershipPage.comingSoon")}</p>
        </Banner>

        <section>
          <dl className="m-0 flex flex-col gap-4">
            <MembershipDetail
              label={t("membershipPage.billingName")}
              value={viewModel.billingName}
            />
            <MembershipDetail
              label={t("membershipPage.billingAddress")}
              value={viewModel.billingDetails.map((line) => (
                <div key={line}>{line}</div>
              ))}
            />
            <MembershipDetail
              label={t("membershipPage.dues")}
              value={viewModel.duesDetail}
            />
            <MembershipDetail
              label={t("membershipPage.paymentMethod")}
              value={viewModel.paymentMethod}
              valueClassName="whitespace-pre-line"
            />
            {viewModel.cardExpiration && (
              <MembershipDetail
                label={t("membershipPage.cardExpiration")}
                value={
                  viewModel.cardExpiration ??
                  t("dashboard.personalInformation.missingValue")
                }
              />
            )}
            <MembershipDetail
              label={t("membershipPage.nextPaymentDate")}
              value={viewModel.nextPaymentDate}
            />
          </dl>
        </section>

        <section className={sectionClassName}>
          <h2 className={sectionHeadingClassName}>
            {t("membershipPage.paymentHistory")}
          </h2>
          {paymentHistory.length > 0 ? (
            <ul className="m-0 list-none rounded-md bg-gray-50 p-0">
              {paymentHistory.map((payment) => (
                <li
                  className="grid gap-1 border-b border-gray-200 px-4 py-3 last:border-b-0 sm:grid-cols-[8rem_minmax(0,1fr)_auto] sm:gap-4"
                  key={payment.key}
                >
                  <p className={cn("m-0", typography.formPageSubtitle.className)}>
                    {payment.date}
                  </p>
                  <p
                    className={cn(
                      "m-0 wrap-anywhere",
                      typography.formPageSubtitle.className,
                    )}
                  >
                    {payment.paymentMethod}
                    <span className="text-body-sm ml-3 font-thin text-gray-900">
                      {t(`membershipPage.${payment.paymentType}`)}
                    </span>
                  </p>
                  <p className="text-body-sm m-0 text-gray-900">{payment.amount}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-body-sm m-0 rounded-md bg-gray-50 px-4 py-3 text-gray-900">
              {t("membershipPage.noPaymentHistory")}
            </p>
          )}
        </section>

        {membershipActionKeys.length > 0 && (
          <section className={sectionClassName}>
            <h2 className={sectionHeadingClassName}>
              {t("membershipPage.actions.title")}
            </h2>
            <div className="grid gap-3">
              {membershipActionKeys.map((key) =>
                key === "cancelDues" && onCancelDues ? (
                  <ActionListButton key={key} onClick={onCancelDues}>
                    {actions[key].label}
                  </ActionListButton>
                ) : (
                  <MembershipActionItem href={actions[key].href} key={key}>
                    {actions[key].label}
                  </MembershipActionItem>
                ),
              )}
            </div>
          </section>
        )}
      </Card>
    </PageShell>
  );
};

const MembershipDetail = ({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: ReactNode;
  valueClassName?: string;
}) => (
  <div className="grid gap-1 sm:grid-cols-[12rem_minmax(0,1fr)] sm:gap-4">
    <dt className={typography.profileDetail.className}>{label}</dt>
    <dd className={`${detailValueClassName} ${valueClassName ?? ""}`}>{value}</dd>
  </div>
);

const MembershipActionItem = ({
  children,
  href,
}: {
  children: ReactNode;
  href: string;
}) => {
  return (
    <ActionListLink
      href={href}
      icon={<ExternalLink className="size-5 shrink-0 text-current" />}
      {...externalLinkProps}
    >
      {children}
    </ActionListLink>
  );
};

export default MembershipPageLayout;
